import React, {
  useMemo,
  useId,
  useState,
  type ReactNode,
  useEffect,
  Fragment,
} from "react";
import {
  IconNameEnum,
  useModuleBEM,
  useUiStyles,
} from "@b2bportal/core-themes";
import { CoreUiComponents, type InputErrorType } from "@b2bportal/core-types";
import * as SelectPrimitive from "@radix-ui/react-select";
import { InputContainer, InputError } from "@tenant/ui";
import { Icon } from "@tenant/ui";
import clsx from "clsx";
import defaultStyles from "./Select.module.scss";

export interface SelectComponentProps extends SelectPrimitive.SelectProps {
  placeholder?: React.ReactNode;
  id?: string;
  label?: string;
  // Radix Content prop
  contentProps?: SelectPrimitive.SelectContentProps;
  className?: string;
  valueToDisplay?: React.ReactNode;
  startIcon?: ReactNode;
  error?: InputErrorType;
  disablePortal?: boolean;
  hasInlineLabel?: boolean;
  style: "thin" | "default";
  required?: boolean;
}

export const Select = React.forwardRef<HTMLButtonElement, SelectComponentProps>(
  (
    {
      children,
      placeholder,
      valueToDisplay,
      contentProps,
      className,
      hasInlineLabel = false,
      id,
      label,
      startIcon,
      error,
      disablePortal = false,
      disabled = false,
      required = false,
      style = "default",
      ...props
    },
    forwardedRef
  ) => {
    const COMPONENT_KEY = CoreUiComponents.Select;
    const styles = useUiStyles(COMPONENT_KEY, defaultStyles);
    const [_block, cn] = useModuleBEM(styles, COMPONENT_KEY);

    const reactId = useId();
    const internalId = useMemo(() => {
      return id ?? reactId;
    }, [id, reactId]);

    const [internalValue, setInternalValue] = useState(
      props.value ?? props.defaultValue
    );
    const hasValue = useMemo(() => {
      return internalValue != null && internalValue !== "";
    }, [internalValue]);

    useEffect(() => {
      setInternalValue(props.value);
    }, [props.value]);

    const Portal = disablePortal ? Fragment : SelectPrimitive.Portal;

    return (
      <div className={cn("container")}>
        {!hasInlineLabel && label && (
          <label className={cn("label", { thin: style === "thin" })}>
            {label}
            {required && <span className={cn("label-required")}>*</span>}
          </label>
        )}
        <SelectPrimitive.Root
          {...props}
          disabled={disabled}
          onValueChange={(e) => {
            setInternalValue(e);
            props.onValueChange?.(e);
          }}
        >
          <div className={cn("root")}>
            <SelectPrimitive.Trigger
              ref={forwardedRef}
              className={clsx(cn("trigger", { disabled }), className)}
            >
              <InputContainer
                className={cn("input-container", { thin: style === "thin" })}
                id={internalId}
                label={!hasInlineLabel || !label ? undefined : label}
                hasValue={hasValue}
                startContent={startIcon}
                hasError={error != null}
                endContent={<Icon iconName={IconNameEnum.chevronDown} />}
              >
                <div
                  className={cn("trigger-label", {
                    "select-label": label != null,
                    "has-inline-label": hasInlineLabel,
                  })}
                >
                  <SelectPrimitive.Value
                    placeholder={<span>{placeholder}</span>}
                    aria-label={valueToDisplay}
                  >
                    {valueToDisplay}
                  </SelectPrimitive.Value>
                </div>
              </InputContainer>
            </SelectPrimitive.Trigger>

            <Portal>
              <SelectPrimitive.Content
                className={cn("content")}
                position="popper"
                sideOffset={5}
                arrowPadding={5}
                {...contentProps}
              >
                <SelectPrimitive.Viewport className={cn("view")}>
                  {children}
                </SelectPrimitive.Viewport>
              </SelectPrimitive.Content>
            </Portal>
            <InputError error={error} />
          </div>
        </SelectPrimitive.Root>
      </div>
    );
  }
);

export const SelectItem = React.forwardRef<
  HTMLDivElement,
  SelectPrimitive.SelectItemProps
>(({ children, className, ...props }, forwardedRef) => {
  const COMPONENT_KEY = CoreUiComponents.Select;
  const styles = useUiStyles(COMPONENT_KEY, defaultStyles);
  const [_block, cn] = useModuleBEM(styles, COMPONENT_KEY);

  return (
    <SelectPrimitive.Item
      {...props}
      ref={forwardedRef}
      className={clsx(cn("item"), className)}
    >
      <SelectPrimitive.ItemText>
        <span>{children}</span>
      </SelectPrimitive.ItemText>
      <SelectPrimitive.ItemIndicator>
        <Icon
          iconName={IconNameEnum.check}
          className={cn("item-indicator-icon")}
        />
      </SelectPrimitive.ItemIndicator>
    </SelectPrimitive.Item>
  );
});
